import { AuthenticateWithRedirectCallback, useAuth, useUser } from "@clerk/clerk-react";
import { ThemeProvider } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import {
  Fragment,
  lazy,
  type ReactNode,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
  useNavigationType,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { HelpCenter } from "@/components/help/HelpCenter";
import { usePermissions } from "@/hooks/usePermissions";
import {
  clearAnalyticsUser,
  consumePendingSignUp,
  isPageViewState,
  pushSignUp,
  pushVirtualPageView,
  setAnalyticsUser,
} from "@/lib/analytics";
import { setTokenProvider } from "@/lib/apiClient";
import posthog from "@/lib/posthog";
import Accounting from "@/pages/Accounting";
import CampaignTester from "@/pages/CampaignTester";
import Reports from "@/pages/Reports";
import System from "@/pages/System";
import ThankYouNotesPage from "@/pages/ThankYouNotesPage";
import { clearStoredImpactReportDeferrals } from "@/utils/impact-report-helper";
import CampaignBuilderBlocked from "./components/campaignBuilder/CampaignBuilderBlocked";
import Drawer from "./components/Drawer";
import NoAccess from "./components/NoAccess";
import { PermissionGuard } from "./components/PermissionGuard";
import { RequireAuth } from "./components/RequireAuth";
import { RequireOnboarded } from "./components/RequireOnboarded";
import { CampaignBuilderProvider, useCampaignBuilder } from "./context/CampaignBuilderContext";
import { CollectionsProvider } from "./context/CollectionsContext";
import { ImpactReportProvider } from "./context/ImpactReportContext";
import { ProductDetailProvider } from "./context/ProductDetailContext";
import Account from "./pages/Account";
import Actions from "./pages/Actions";
import AdminCollectionDetail from "./pages/admin/AdminCollectionDetail";
import AdminCollections from "./pages/admin/AdminCollections";
import AdminContacts from "./pages/admin/AdminContacts";
import CampaignBuilder from "./pages/CampaignBuilder";
import Campaigns from "./pages/Campaigns";
import ChangeTopupCard from "./pages/ChangeTopupCard";
import Collections from "./pages/Collections";
import CollectionTier from "./pages/CollectionTier";
import CollectionStep from "./pages/campaignBuilder/CollectionStep";
import DesignStep from "./pages/campaignBuilder/DesignStep";
import EmailReminderStation from "./pages/campaignBuilder/EmailReminderStation";
import RecipientsStep from "./pages/campaignBuilder/RecipientsStep";
import ReviewStep from "./pages/campaignBuilder/ReviewStep";
import Dashboard from "./pages/Dashboard";
import Finance from "./pages/Finance";
import GfgAdmin from "./pages/GfgAdmin";
import CampaignManager from "./pages/gfgAdmin/CampaignManager";
import ImpactReport from "./pages/ImpactReport";
import ImpactReportJob from "./pages/ImpactReportJob";
import Onboarding from "./pages/Onboarding";
import Profile from "./pages/Profile";
import SignInPage from "./pages/SignIn";
import SignUpPage from "./pages/SignUp";
import SingleCollection from "./pages/SingleCollection";
import Support from "./pages/Support";
import Users from "./pages/Users";
import UsersAndOrgs from "./pages/UsersAndOrgs";
import gfgTheme from "./theme/gfgTheme";

// Public recipient redemption flow (our own real-route entry). Lazy so the recipient
// experience stays out of the authenticated admin bundle and only loads on /g/:token.
const Redemption = lazy(() => import("./pages/redemption/Redemption"));
const DeclineGift = lazy(() => import("./pages/customer/DeclineGift"));
// Public campaign-specific redeem page (/redeem/:code) — the email→resend "Find Your Gift" lookup.
const RedeemLookup = lazy(() => import("./pages/redeem/RedeemLookup"));

/**
 * Mounts the builder's own Help Center (it lives outside `Drawer`, so it has none). The bubble is
 * obscured only when a customize drawer is actually covering it: `designDrawerOpen` is the single
 * open signal for all three customize drawers, but nothing resets it when the user leaves the
 * Design step, so it must also be gated on the route. It's also obscured on the full-screen
 * reminder editor, which covers it the same way (WCAG 2.2 SC 2.4.11). It is also obscured while a
 * full-screen preview (the Design or Review step's FullscreenPreviewOverlay) covers it, and that
 * flag is raised by the overlay's own mount and unmount, so it needs no route gate.
 */
function BuilderHelpCenter({ children }: { children: ReactNode }) {
  const { designDrawerOpen, fullscreenPreviewOpen } = useCampaignBuilder();
  const onDesignStep = useMatch("/campaign/create/design") !== null;
  const onReminders = useMatch("/campaign/create/reminders") !== null;
  return (
    <HelpCenter
      bubbleObscured={(designDrawerOpen && onDesignStep) || onReminders || fullscreenPreviewOpen}
    >
      {children}
    </HelpCenter>
  );
}

function CampaignFlowLayout() {
  return (
    <CampaignBuilderProvider>
      <ProductDetailProvider>
        <BuilderHelpCenter>
          <Outlet />
        </BuilderHelpCenter>
      </ProductDetailProvider>
    </CampaignBuilderProvider>
  );
}

/**
 * Entry route for the Collections "Send This Collection" / "Send in Collection" actions
 * (/campaign/create/tier/:tier?country=XX): pre-selects the budget tier from the route param and,
 * when present, the country from the query, then hands off to Step 1 at the canonical /collection
 * URL. Passing them via the URL (rather than sessionStorage) keeps it self-cleaning. A non-US
 * country only ever arrives here for Pro+ accounts — the caller gates before navigating — so the
 * seeded country is always valid for the plan.
 */
function CampaignBuilderTierPreselect() {
  const { tier } = useParams<{ tier: string }>();
  const [searchParams] = useSearchParams();
  const { setSelectedBudget, setSelectedCountry } = useCampaignBuilder();
  const navigate = useNavigate();
  const seeded = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once seed + redirect; the ref guard prevents re-runs.
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    const budget = Number(tier);
    if (!Number.isNaN(budget)) setSelectedBudget(budget);
    const country = searchParams.get("country");
    if (country) setSelectedCountry(country);
    navigate("/campaign/create/collection", { replace: true });
  }, []);
  return null;
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture("$pageview", { path: location.pathname });
  }, [location.pathname]);
  return null;
}

/**
 * GTM sends the initial page_view itself, so only later navigations push. A navigation starts on a
 * PUSH/POP or on a REPLACE marked with PAGE_VIEW_STATE; an unmarked REPLACE is a redirect and only
 * extends a navigation already open, so the timer reports where it settled. `/` only redirects, so
 * it is never reported and keeps the navigation open until its redirect lands, however long
 * permissions take.
 */
function VirtualPageViewTracker() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastPath = useRef<string | null>(null);
  const isNavigating = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPath.current === null) {
      lastPath.current = path;
      return;
    }
    if (!isNavigating.current) {
      const startsNavigation = navigationType !== "REPLACE" || isPageViewState(location.state);
      if (!startsNavigation || path === lastPath.current) {
        lastPath.current = path;
        return;
      }
      isNavigating.current = true;
    }
    if (timer.current) clearTimeout(timer.current);
    if (location.pathname === "/") return;
    timer.current = setTimeout(() => {
      timer.current = null;
      isNavigating.current = false;
      if (path === lastPath.current) return;
      lastPath.current = path;
      pushVirtualPageView(path);
    }, 100);
  }, [location.pathname, location.search, location.state, navigationType]);

  return null;
}

/**
 * user_id follows the internal user id; the pending sign_up goes out right after it, but never
 * inside onboarding, where the plan can still change.
 */
function DataLayerUserSync() {
  const { userId } = usePermissions();
  const { user } = useUser();
  const { pathname } = useLocation();
  const pushedUserId = useRef<number | null>(null);

  useEffect(() => {
    if (userId === pushedUserId.current) return;
    if (userId === null) clearAnalyticsUser();
    else setAnalyticsUser(userId);
    pushedUserId.current = userId;
  }, [userId]);

  useEffect(() => {
    if (userId === null || !user || pathname.startsWith("/onboarding")) return;
    const pending = consumePendingSignUp(user.id);
    if (pending) pushSignUp(pending.method, pending.plan);
  }, [userId, user, pathname]);

  return null;
}

/**
 * Clears the WHOLE react-query cache when the signed-in Clerk user changes with no page reload —
 * a session expiring, or a sign-out (or a different sign-in) in another tab. The cache carries no
 * user id of its own: a query like `["impactReportJob", jobId]` is held `staleTime: Infinity`
 * (`hooks/useImpactReport.ts`), so within its `gcTime` a second user signing in on the same tab
 * could otherwise be served the first user's cached deferral or completed report — see
 * `utils/impact-report-helper.ts`'s storage-cache banner for the sessionStorage half of this same
 * risk, which this guard does not replace (it only empties that storage too, on the same
 * transitions).
 *
 * Mounted once, here, rather than per-page: `App` renders inside both `QueryClientProvider` and
 * `ClerkProvider` (`index.tsx`), so a component anywhere in this tree already has both contexts —
 * neither provider has to move for this to reach them.
 */
function QueryCacheUserScopeGuard() {
  const { user, isLoaded } = useUser();
  const queryClient = useQueryClient();
  // `undefined` = "no resolved user observed yet", distinct from `null` = "resolved: signed
  // out". Only a change between two RESOLVED ids clears the cache — the very first resolution
  // (mount) never does, or every fresh sign-in would wipe the cache it just started filling.
  const lastResolvedUserId = useRef<string | null | undefined>(undefined);

  // useLayoutEffect, not useEffect: this fires synchronously after the commit that observed the
  // user-id change but BEFORE the browser paints, so a same-tab user-A -> user-B switch never
  // paints a frame holding a stale, still-cached query result. `UserScopedSubtree` below remounts
  // the signed-in tree on the same kind of transition, in its own layout effect in the same
  // commit — together they mean the old subtree is gone and the cache is empty before anything
  // new user B renders reaches the screen.
  useLayoutEffect(() => {
    if (!isLoaded) return;
    const currentUserId = user?.id ?? null;
    const previousUserId = lastResolvedUserId.current;
    lastResolvedUserId.current = currentUserId;

    if (previousUserId === undefined || previousUserId === currentUserId) return;
    // Signed-out -> signed-in is not this guard's case: there is no PRIOR signed-in user's data
    // in the cache to leak (the transition INTO signed-out already cleared it, below).
    if (previousUserId === null) return;

    queryClient.clear();
    // The sessionStorage half: the user who just left must not leave their stored deferrals
    // (job ids, `recipient` email) behind in this tab.
    clearStoredImpactReportDeferrals();
  }, [isLoaded, user?.id, queryClient]);

  return null;
}

/**
 * Remounts the signed-in subtree (ImpactReportProvider, CollectionsProvider, Drawer, and every
 * page below them) whenever the Clerk user id changes to a DIFFERENT resolved id, so no
 * component instance — and no component state, like ImpactReportProvider's selected campaign —
 * survives a same-tab user switch. Pairs with `QueryCacheUserScopeGuard`: that guard empties the
 * shared query cache; this remounts the tree that reads it, so nothing under the new key can even
 * attempt to render with a query still resolved from the old cache.
 *
 * The key only changes between two RESOLVED, DIFFERENT, non-null ids. `RequireAuth` above this
 * gates on `useAuth().isSignedIn`, but this reads `useUser()` — Clerk resolves the two hooks'
 * `isLoaded` independently, so `useUser()`'s id can still be settling on the render(s) right
 * after `RequireAuth` first lets this subtree mount. Treating that initial undefined -> id
 * resolution as a "switch" would remount (and drop the state of) the tree on every fresh
 * sign-in, which is exactly the churn this must not cause.
 *
 * useLayoutEffect (not useEffect), same reasoning as `QueryCacheUserScopeGuard` above: bumping
 * the key here has to land in the same before-paint commit as that guard's `queryClient.clear()`
 * so the browser only ever paints the new user's first render, never a frame of the old subtree
 * left standing on stale data.
 */
function UserScopedSubtree({ children }: { children: React.ReactNode }) {
  const { user, isLoaded } = useUser();
  const lastResolvedUserId = useRef<string | null | undefined>(undefined);
  const [userKey, setUserKey] = useState(0);

  useLayoutEffect(() => {
    if (!isLoaded) return;
    const currentUserId = user?.id ?? null;
    const previousUserId = lastResolvedUserId.current;
    lastResolvedUserId.current = currentUserId;

    if (previousUserId === undefined || previousUserId === currentUserId) return;
    if (previousUserId === null || currentUserId === null) return;

    setUserKey((key) => key + 1);
  }, [isLoaded, user?.id]);

  return <Fragment key={userKey}>{children}</Fragment>;
}

/**
 * Landing redirect — sends the user to their best home based on capability, not role:
 * dashboard if they can see it, else the catalog. If they can view NEITHER, render a
 * terminal NoAccess page rather than redirecting — otherwise a capless user bounces
 * / → /collections → /dashboard → / forever (every guarded route redirects back here).
 */
function LandingRedirect() {
  const { can, isGfg, isLoaded } = usePermissions();
  if (!isLoaded) return null;
  if (isGfg) return <Navigate to="/gfg-dashboard" replace />;
  if (can("view", "DASHBOARD")) return <Navigate to="/dashboard" replace />;
  if (can("view", "COLLECTION")) return <Navigate to="/collections" replace />;
  return <NoAccess />;
}

/**
 * GFG Admins are redirected from /collections to their Collections Settings page
 * (/admin/collections). Client users (who already passed the COLLECTION/view guard above
 * this) keep the real page unchanged.
 */
function CollectionsGate() {
  const { isGfg, isLoaded } = usePermissions();
  if (!isLoaded) return null;
  return isGfg ? <Navigate to="/admin/collections" replace /> : <Collections />;
}

/**
 * Campaign Manager isn't built for GFG Admins yet either — they get the two-tab (Send
 * Queue / Campaigns) Coming Soon placeholder from the reference demo's admin tab bar.
 * Client users keep the real Campaigns page at the same route, unchanged.
 */
function CampaignManagerGate() {
  const { isGfg, isLoaded } = usePermissions();
  if (!isLoaded) return null;
  return isGfg ? <CampaignManager /> : <Campaigns />;
}

export default function App() {
  const { getToken } = useAuth();

  useLayoutEffect(() => {
    setTokenProvider(() => getToken({ template: "gfgf-api" }));
  }, [getToken]);

  return (
    <ThemeProvider theme={gfgTheme}>
      <BrowserRouter>
        <PageTracker />
        <VirtualPageViewTracker />
        <DataLayerUserSync />
        <QueryCacheUserScopeGuard />
        <Routes>
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />

          {/* The impact report's control bar renders inside `Drawer.tsx`'s `Toolbar`, ABOVE this
              route's `<Outlet />`, while the renderers live in the page BELOW it — so the control
              state is mounted here, around both. It holds state and fetches nothing, which is why
              wrapping every authenticated route costs the other routes nothing. Same shape as
              `CampaignBuilderProvider` around the campaign flow. */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <UserScopedSubtree>
                  {/* Signed in is not the same as onboarded: a client who backs out of the wizard has
                      no organization, and every route below needs one. */}
                  <RequireOnboarded>
                    <ImpactReportProvider>
                      <CollectionsProvider>
                        <Drawer />
                      </CollectionsProvider>
                    </ImpactReportProvider>
                  </RequireOnboarded>
                </UserScopedSubtree>
              </RequireAuth>
            }
          >
            {/* Default landing route — capability-based redirect. */}
            <Route index element={<LandingRedirect />} />

            <Route
              path="dashboard"
              element={
                <PermissionGuard action="view" subject="DASHBOARD" redirect="/">
                  <Dashboard />
                </PermissionGuard>
              }
            />
            <Route
              path="campaigns"
              element={
                <PermissionGuard action="view" subject="CAMPAIGN" redirect="/dashboard">
                  <CampaignManagerGate />
                </PermissionGuard>
              }
            />
            {/* The screen a client lands on from the banner, the campaign menu or the alert email.
                `edit CAMPAIGN` mirrors the API guard on PATCH /topup-payment-method, so the page is
                never shown to someone whose save would 403. */}
            <Route
              path="campaigns/:campaignId/payment-card"
              element={
                <PermissionGuard action="edit" subject="CAMPAIGN" redirect="/campaigns">
                  <ChangeTopupCard />
                </PermissionGuard>
              }
            />
            <Route
              path="thanks-report"
              element={
                <PermissionGuard action="view" subject="REPORT" redirect="/dashboard">
                  <ThankYouNotesPage />
                </PermissionGuard>
              }
            />
            <Route
              path="impact"
              element={
                <PermissionGuard action="view" subject="REPORT" redirect="/dashboard">
                  <ImpactReport />
                </PermissionGuard>
              }
            />
            <Route
              path="impact/reports/:jobId"
              element={
                <PermissionGuard action="view" subject="REPORT" redirect="/dashboard">
                  <ImpactReportJob />
                </PermissionGuard>
              }
            />
            <Route
              path="reports"
              element={
                <PermissionGuard action="view" subject="REPORT" redirect="/dashboard">
                  <Reports />
                </PermissionGuard>
              }
            />
            <Route
              path="collections"
              element={
                <PermissionGuard action="view" subject="COLLECTION" redirect="/dashboard">
                  <CollectionsGate />
                </PermissionGuard>
              }
            />
            {/* Tier detail — the composed product feed for one budget tier. Static `tier`
                segment is registered before `:id` so it can't be captured as a collection id. */}
            <Route
              path="collections/tier/:budget"
              element={
                <PermissionGuard action="view" subject="COLLECTION" redirect="/dashboard">
                  <CollectionTier />
                </PermissionGuard>
              }
            />
            <Route
              path="collections/:id"
              element={
                <PermissionGuard action="view" subject="COLLECTION" redirect="/dashboard">
                  <SingleCollection />
                </PermissionGuard>
              }
            />
            {/* Cross-org invoice blotter — GFG only, FINANCE/view pinned to scope "all" like
                Accounting beside it. The ROUTE moves with the nav item, not just the link: hiding a
                menu entry while leaving the URL reachable is decoration, not a restriction. A client
                reaches their own invoices from Account, on the Invoices tab. */}
            <Route
              path="finance"
              element={
                <PermissionGuard action="view" subject="FINANCE" scope="all" redirect="/dashboard">
                  <Finance />
                </PermissionGuard>
              }
            />
            {/* Action Dashboard — the approval queue. Gated on approve/CREDIT at scope "all", the
                same capability the API pins on /admin/approvals. Membership in `approvers` decides
                who can actually act; this only decides who sees the surface. */}
            <Route
              path="actions"
              element={
                <PermissionGuard
                  action="approve"
                  subject="CREDIT"
                  scope="all"
                  redirect="/dashboard"
                >
                  <Actions />
                </PermissionGuard>
              }
            />
            {/* Accounting subledger — GFG-only. FINANCE/view pinned to scope "all" on the API too,
                since these are GFG's books across every client. */}
            <Route
              path="accounting"
              element={
                <PermissionGuard action="view" subject="FINANCE" redirect="/dashboard">
                  <Accounting />
                </PermissionGuard>
              }
            />
            {/* GFG-only test harness. The API returns 404 unless ENABLE_CAMPAIGN_TESTER=true, so a
                stale link degrades to the "not enabled" notice rather than a blank screen.
                Deleted with the rest of the tester. */}
            <Route
              path="campaign-tester"
              element={
                <PermissionGuard action="view" subject="FINANCE" scope="all" redirect="/dashboard">
                  <CampaignTester />
                </PermissionGuard>
              }
            />
            {/* Platform settings — GFG's own configuration. Super-admin only, the same gate the API
                enforces on /admin/system. */}
            <Route
              path="system"
              element={
                <PermissionGuard
                  action="modify"
                  subject="CHARITY_DESTINATION"
                  scope="all"
                  redirect="/dashboard"
                >
                  <System />
                </PermissionGuard>
              }
            />
            <Route path="account" element={<Account />} />
            <Route path="profile" element={<Profile />} />
            <Route
              path="settings"
              element={
                <PermissionGuard action="view" subject="COMPANY" redirect="/dashboard">
                  <div>Settings (Coming Soon)</div>
                </PermissionGuard>
              }
            />
            {/* Support & logout — available to any authenticated user (no capability gate). */}
            <Route path="support" element={<Support />} />
            <Route path="logout" element={<div>Logout (Coming Soon)</div>} />
            <Route
              path="users"
              element={
                <PermissionGuard action="view" subject="USER" redirect="/dashboard">
                  <Users />
                </PermissionGuard>
              }
            />
            {/* GFG admin console — gated on view USER (scope call deferred; observing GFG vs CLIENT_ADMIN). */}
            <Route
              path="users-orgs"
              element={
                <PermissionGuard action="view" subject="USER" redirect="/dashboard">
                  <UsersAndOrgs />
                </PermissionGuard>
              }
            />
            <Route
              path="gfg-dashboard"
              element={
                <PermissionGuard
                  action="view"
                  subject="DASHBOARD"
                  scope="all"
                  redirect="/dashboard"
                >
                  <GfgAdmin />
                </PermissionGuard>
              }
            />
            {/* GFG-only placeholders — USER/view/all is a GFG-exclusive scope (Client Admins only
                hold "org"), matching how gfg-dashboard pins to GFG. */}
            <Route
              path="audit-log"
              element={
                <PermissionGuard action="view" subject="USER" scope="all" redirect="/dashboard">
                  <div>Audit Log (Coming Soon)</div>
                </PermissionGuard>
              }
            />
            <Route
              path="contacts"
              element={
                <PermissionGuard action="view" subject="USER" scope="all" redirect="/dashboard">
                  <AdminContacts />
                </PermissionGuard>
              }
            />
            <Route
              path="admin/collections"
              element={
                <PermissionGuard
                  action="view"
                  subject="COLLECTION"
                  scope="all"
                  redirect="/dashboard"
                >
                  <AdminCollections />
                </PermissionGuard>
              }
            />
            <Route
              path="admin/collections/:collectionId"
              element={
                <PermissionGuard
                  action="view"
                  subject="COLLECTION"
                  scope="all"
                  redirect="/dashboard"
                >
                  <AdminCollectionDetail />
                </PermissionGuard>
              }
            />
          </Route>

          {/* Campaign Builder is rendered in a provider for modal state management and mounts its own Help Center, since it sits outside Drawer. */}
          <Route
            element={
              <RequireAuth>
                <CampaignFlowLayout />
              </RequireAuth>
            }
          >
            <Route
              path="campaign/create"
              element={
                /* `clientOnly`: hiding the topbar button is not enough, because this route stays
                   reachable by typing its URL — and every child step, including the tier entry
                   `sendCollection()` routes to, is nested under it. GFG staff reach the builder by
                   impersonating the client, which turns this guard off for them. A client who
                   can't create campaigns is told why instead of being redirected. */
                <PermissionGuard
                  action="create"
                  subject="CAMPAIGN"
                  clientOnly
                  fallback={<CampaignBuilderBlocked />}
                >
                  <CampaignBuilder />
                </PermissionGuard>
              }
            >
              <Route index element={<CollectionStep />} />
              <Route path="collection" element={<CollectionStep />} />
              {/* Tier pre-select entry — seeds the budget from the param, then redirects to Step 1. */}
              <Route path="tier/:tier" element={<CampaignBuilderTierPreselect />} />

              <Route path="recipients" element={<RecipientsStep />} />
              <Route path="design" element={<DesignStep />} />
              <Route path="review" element={<ReviewStep />} />
              {/* Reminder email editor — full screen, renders over the stepper via fixed positioning */}
              <Route
                path="reminders"
                element={
                  <PermissionGuard action="edit" subject="CAMPAIGN" redirect="/dashboard">
                    <EmailReminderStation />
                  </PermissionGuard>
                }
              />
            </Route>
          </Route>
          {/* SSO callback — handles OAuth redirect-back from Google / Microsoft */}
          <Route
            path="/sso-callback"
            element={<AuthenticateWithRedirectCallback redirectUrl="/onboarding" />}
          />

          {/* Onboarding wizard — full-screen, no sidebar, shown after first sign-up */}
          <Route
            path="/onboarding"
            element={
              <RequireAuth>
                <Onboarding />
              </RequireAuth>
            }
          />

          {/* Public recipient redemption flow — the token IS the credential.
              Mounted OUTSIDE RequireAuth/RoleGuard: recipients are not Clerk
              users. Reached via the /g/:token link emailed to the recipient, or the
              /u/:token universal link the client shares publicly. Both render the same
              flow — the API resolves by token, so the path prefix is cosmetic. */}
          <Route
            path="/g/:token/*"
            element={
              <Suspense fallback={null}>
                <Redemption />
              </Suspense>
            }
          />
          <Route
            path="/u/:token/*"
            element={
              <Suspense fallback={null}>
                <Redemption />
              </Suspense>
            }
          />

          {/* Public campaign-specific redeem page — same arrangement as /g/:token: no Clerk, the
              permanent `:code` in the URL is the only input. Printed on physical materials / QR codes,
              so it must be stable and work with no login. */}
          <Route
            path="/redeem/:code"
            element={
              <Suspense fallback={null}>
                <RedeemLookup />
              </Suspense>
            }
          />

          {/* Public "decline my gift" page — same arrangement as /g/:token above: the signed
              ?token= from the reminder email is the credential, so this sits OUTSIDE RequireAuth.
              The path and query-string shape are v1's on purpose — links already in recipients'
              inboxes (getDeclineGiftUrl) point here. */}
          <Route
            path="/customer/decline-gift"
            element={
              <Suspense fallback={null}>
                <DeclineGift />
              </Suspense>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
